/*
 * Featured slideshow — progressive enhancement only.
 * Slides are server-rendered and crawlable; with JS off the viewport is a horizontal
 * scroll-snap fallback and every link works. This upgrades it into an accessible carousel:
 * transform track, inert/aria-hidden on inactive slides, keyboard, aria-live announcements
 * (silenced while autoplay rotates, polite when paused/manual), autoplay disabled under
 * prefers-reduced-motion and paused on hover/focus/visibilitychange (resumes only when BOTH
 * pointer and focus have left), a WCAG 2.2.2 visible pause/play toggle whose state is sticky for
 * the session, and theme-editor block/section select handling.
 *
 * Loaded only when the section has >1 slide. Guarded so duplicate instances can't double-define.
 */
if (!customElements.get('featured-slideshow')) {
  class FeaturedSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('.fspot__track');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.fspot__slide'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-fspot-dot]'));
      this.live = this.querySelector('.fspot__live');
      this.count = this.querySelector('[data-fspot-count]');
      this.pauseBtn = this.querySelector('[data-fspot-pause]');
      this.n = this.slides.length;
      if (!this.track || this.n < 2) return;

      this.index = 0;
      this.timer = null;
      this.userPaused = false; // sticky, session-persisted
      this.editorPause = false; // theme-editor block/section selected
      this.hovered = false;
      this.hasFocus = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayWanted = this.dataset.autoplay === 'true';
      this.speed = (parseFloat(this.dataset.speed) || 6) * 1000;
      this.slideWord = this.dataset.slideWord || 'Slide';
      this.ofWord = this.dataset.ofWord || 'of';

      // Restore the sticky (user-set) pause state for this browsing session.
      var section = this.closest('.fspot');
      this.storeKey = 'fspot-paused:' + ((section && section.id) || 'default');
      try {
        if (sessionStorage.getItem(this.storeKey) === '1') this.userPaused = true;
      } catch (e) { /* storage blocked (private mode) — fall back to not paused */ }

      this.classList.add('is-enhanced');
      this.bind();
      this.render(0, false); // apply initial inert/dot state without announcing on load
      this.updatePauseButton();
      this.start();
    }

    disconnectedCallback() {
      // The theme editor tears down and re-renders sections; remove document-level listeners and
      // the timer so nothing leaks onto the discarded element.
      this.stop();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this._onMotion && this.motionQuery) {
        if (this.motionQuery.removeEventListener) this.motionQuery.removeEventListener('change', this._onMotion);
        else if (this.motionQuery.removeListener) this.motionQuery.removeListener(this._onMotion);
      }
      if (this._editorBound) {
        document.removeEventListener('shopify:block:select', this._onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
        document.removeEventListener('shopify:section:select', this._onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
      }
    }

    bind() {
      var self = this;
      var next = this.querySelector('[data-fspot-next]');
      var prev = this.querySelector('[data-fspot-prev]');
      if (next) next.addEventListener('click', function () { self.go(self.index + 1); });
      if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); });
      this.dots.forEach(function (d) {
        d.addEventListener('click', function () { self.go(Number(d.dataset.fspotDot)); });
      });
      if (this.pauseBtn) this.pauseBtn.addEventListener('click', function () { self.toggleUserPause(); });

      // BG-105 r4 N9: touch swipe once enhanced (overflow hidden removed the native scroll)
      var vp = this.querySelector('.fspot__viewport');
      if (vp) {
        var sx = 0, sy = 0, tid = null;
        vp.addEventListener('touchstart', function (e) {
          if (e.touches.length === 1) { tid = e.touches[0].identifier; sx = e.touches[0].clientX; sy = e.touches[0].clientY; }
          else tid = null; // a second finger cancels the gesture (pinch)
        }, { passive: true });
        vp.addEventListener('touchcancel', function () { tid = null; }, { passive: true });
        vp.addEventListener('touchend', function (e) {
          if (tid === null) return;
          if (e.touches.length > 0) { tid = null; return; } // another finger is still down
          var t = null;
          for (var i = 0; i < e.changedTouches.length; i++) if (e.changedTouches[i].identifier === tid) t = e.changedTouches[i];
          if (!t) return;
          tid = null;
          var dx = t.clientX - sx, dy = t.clientY - sy;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            self.go(self.index + (dx < 0 ? 1 : -1)); self.stop(); self.start();
          }
        }, { passive: true });
      }

      this.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); self.go(self.index + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); self.go(self.index - 1); }
      });

      // Transient pauses. Autoplay resumes only once BOTH the pointer and focus have left, so a
      // click/keypress inside the carousel never re-arms rotation while the user is still there.
      this.addEventListener('mouseenter', function () { self.hovered = true; self.stop(); });
      this.addEventListener('mouseleave', function () { self.hovered = false; self.start(); });
      this.addEventListener('focusin', function () { self.hasFocus = true; self.stop(); });
      this.addEventListener('focusout', function () { self.hasFocus = false; self.start(); });

      this._onVisibility = function () { if (document.hidden) self.stop(); else self.start(); };
      document.addEventListener('visibilitychange', this._onVisibility);

      // Honor a mid-session switch to reduced motion.
      this._onMotion = function () { if (self.motionQuery.matches) self.stop(); else self.start(); };
      if (this.motionQuery.addEventListener) this.motionQuery.addEventListener('change', this._onMotion);
      else if (this.motionQuery.addListener) this.motionQuery.addListener(this._onMotion);

      // Theme editor: reveal the slide a merchant selects and don't rotate past it.
      if (window.Shopify && window.Shopify.designMode) {
        this._editorBound = true;
        this._onBlockSelect = function (e) {
          var i = self.slides.indexOf(e.target);
          if (i === -1) return;
          self.editorPause = true;
          self.go(i);
          self.stop();
        };
        this._onBlockDeselect = function (e) {
          if (self.slides.indexOf(e.target) === -1) return;
          self.editorPause = false;
          self.start();
        };
        this._onSectionSelect = function (e) {
          if (!e.target.contains(self)) return;
          self.editorPause = true;
          self.stop();
        };
        this._onSectionDeselect = function (e) {
          if (!e.target.contains(self)) return;
          self.editorPause = false;
          self.start();
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);
        document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
        document.addEventListener('shopify:section:select', this._onSectionSelect);
        document.addEventListener('shopify:section:deselect', this._onSectionDeselect);
      }
    }

    go(to) {
      this.index = (to + this.n) % this.n;
      this.render(this.index, true);
    }

    render(i, announce) {
      // If focus is inside the OUTGOING slide, move it into the incoming slide BEFORE the others go
      // inert — otherwise inert-ing the focused slide drops focus to <body> and keyboard control dies.
      var activeEl = document.activeElement;
      var focusInSlide = this.slides.some(function (s) { return s.contains(activeEl); });

      this.track.style.transform = 'translateX(-' + (i * 100) + '%)';

      var incoming = this.slides[i];
      incoming.inert = false;
      incoming.setAttribute('aria-hidden', 'false');
      incoming.classList.add('is-active');
      incoming.setAttribute('tabindex', '-1');
      if (focusInSlide) incoming.focus({ preventScroll: true });

      this.slides.forEach(function (s, k) {
        if (k === i) return;
        s.inert = true;
        s.setAttribute('aria-hidden', 'true');
        s.classList.remove('is-active');
        s.removeAttribute('tabindex');
      });

      this.dots.forEach(function (d, k) {
        var active = k === i;
        d.classList.toggle('is-active', active);
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });

      if (this.count) this.count.textContent = (i + 1) + ' / ' + this.n;

      if (announce && this.live) {
        this.live.textContent = this.slideWord + ' ' + (i + 1) + ' ' + this.ofWord + ' ' + this.n;
      }
    }

    canAutoplay() {
      return this.autoplayWanted && !this.userPaused && !this.editorPause &&
        !this.hovered && !this.hasFocus && !document.hidden &&
        !this.motionQuery.matches && this.n > 1;
    }

    start() {
      if (this.canAutoplay() && !this.timer) {
        var self = this;
        this.timer = setInterval(function () { self.go(self.index + 1); }, this.speed);
      }
      this.updateLiveMode();
    }

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
      this.updateLiveMode();
    }

    // aria-live OFF while autoplay is actively rotating (don't interrupt SR users every cycle),
    // POLITE when paused or under manual control (so navigation IS announced).
    updateLiveMode() {
      if (this.live) this.live.setAttribute('aria-live', this.timer ? 'off' : 'polite');
    }

    toggleUserPause() {
      this.userPaused = !this.userPaused;
      try { sessionStorage.setItem(this.storeKey, this.userPaused ? '1' : '0'); } catch (e) { /* ignore */ }
      if (this.userPaused) this.stop(); else this.start();
      this.updatePauseButton();
    }

    updatePauseButton() {
      // Stable accessible name ("Pause slideshow"); aria-pressed conveys the paused state and the
      // CSS swaps the pause/play icon. This is the standard toggle-button pattern.
      if (this.pauseBtn) this.pauseBtn.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
    }
  }

  customElements.define('featured-slideshow', FeaturedSlideshow);
}
